/**
 * دورة حياة المستأجر والاشتراك.
 *
 * القيم بحروف كبيرة لتطابق أنواع enum في قاعدة البيانات حرفيًا. أي اختلاف
 * هنا يفرض طبقة تحويل بين الخادم والقاعدة، وهي مصدر أخطاء صامتة عند إضافة
 * حالة جديدة يُنسى تحويلها.
 *
 * المسار الطبيعي:
 *   trial (14 يومًا) → active → past_due (مهلة 7 أيام) → suspended → cancelled
 *
 * الوصول:
 *   full  — قراءة وكتابة كاملة
 *   read  — قراءة وتصدير فقط، لا إنشاء ولا تعديل
 *   none  — لا وصول
 */

export const ACCESS_LEVELS = ['full', 'read', 'none'] as const;
export type AccessLevel = (typeof ACCESS_LEVELS)[number];

import type { StatusKind } from './common.js';

/* ==========================================================================
   حالة المستأجر
   ========================================================================== */

export const TENANT_STATUSES = [
  'TRIAL',
  'ACTIVE',
  'PAST_DUE',
  'SUSPENDED',
  'CANCELLED',
] as const;

export type TenantStatus = (typeof TENANT_STATUSES)[number];

export interface TenantStatusDef {
  key: TenantStatus;
  label: string;
  kind: StatusKind;
  access: AccessLevel;
}

export const TENANT_STATUS_DEFS: readonly TenantStatusDef[] = [
  { key: 'TRIAL', label: 'فترة تجريبية', kind: 'info', access: 'full' },
  { key: 'ACTIVE', label: 'نشط', kind: 'success', access: 'full' },
  { key: 'PAST_DUE', label: 'متأخر السداد', kind: 'warning', access: 'full' },
  { key: 'SUSPENDED', label: 'موقوف', kind: 'danger', access: 'read' },
  { key: 'CANCELLED', label: 'ملغى', kind: 'neutral', access: 'read' },
] as const;

export const TENANT_STATUS_MAP: Readonly<Record<TenantStatus, TenantStatusDef>> =
  Object.fromEntries(TENANT_STATUS_DEFS.map((s) => [s.key, s])) as Record<
    TenantStatus,
    TenantStatusDef
  >;

/** مستوى الوصول المسموح به لحالة مستأجر معيّنة. */
export function accessFor(status: TenantStatus): AccessLevel {
  return TENANT_STATUS_MAP[status].access;
}

/** هل يُسمح بعمليات الكتابة في هذه الحالة؟ */
export function canWrite(status: TenantStatus): boolean {
  return accessFor(status) === 'full';
}

/* ==========================================================================
   حالة الاشتراك
   ========================================================================== */

export const SUBSCRIPTION_STATUSES = [
  'TRIALING',
  'ACTIVE',
  'PAST_DUE',
  'CANCELED',
] as const;

export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

export interface SubscriptionStatusDef {
  key: SubscriptionStatus;
  label: string;
  kind: StatusKind;
}

export const SUBSCRIPTION_STATUS_DEFS: readonly SubscriptionStatusDef[] = [
  { key: 'TRIALING', label: 'تجريبي', kind: 'info' },
  { key: 'ACTIVE', label: 'نشط', kind: 'success' },
  { key: 'PAST_DUE', label: 'متأخر', kind: 'warning' },
  { key: 'CANCELED', label: 'ملغى', kind: 'neutral' },
] as const;

export const SUBSCRIPTION_STATUS_MAP: Readonly<
  Record<SubscriptionStatus, SubscriptionStatusDef>
> = Object.fromEntries(
  SUBSCRIPTION_STATUS_DEFS.map((s) => [s.key, s]),
) as Record<SubscriptionStatus, SubscriptionStatusDef>;

/* ==========================================================================
   ثوابت دورة الحياة
   ========================================================================== */

export const LIFECYCLE = {
  /** مدة الفترة التجريبية بالأيام. */
  trialDays: 14,
  /** مهلة السداد بعد استحقاق الفاتورة وقبل الإيقاف. */
  graceDays: 7,
  /** مدة الاحتفاظ بالبيانات بعد الإلغاء قبل الحذف النهائي. */
  dataRetentionDays: 90,
} as const;

/* ==========================================================================
   الهوية البصرية الافتراضية للمستأجر الجديد
   ========================================================================== */

export interface TenantBranding {
  primary: string;
  accent: string;
  ink: string;
  logoUrl: string;
  markUrl: string;
  stampUrl: string;
  documentHeader: string;
  documentFooter: string;
}

export const DEFAULT_BRANDING: TenantBranding = {
  primary: '#3672EA',
  accent: '#8CE553',
  ink: '#12214A',
  logoUrl: '',
  markUrl: '',
  stampUrl: '',
  documentHeader: '',
  documentFooter: '',
};

/* ==========================================================================
   طلبات التسجيل
   ========================================================================== */

export const SIGNUP_STATUSES = [
  'NEW',
  'TRIALING',
  'CONTACTED',
  'CONVERTED',
  'REJECTED',
] as const;

export type SignupStatus = (typeof SIGNUP_STATUSES)[number];

export interface SignupStatusDef {
  key: SignupStatus;
  label: string;
  kind: StatusKind;
  hint: string;
}

export const SIGNUP_STATUS_DEFS: readonly SignupStatusDef[] = [
  { key: 'NEW', label: 'جديد', kind: 'info', hint: 'وصل ولم يُراجَع بعد.' },
  { key: 'TRIALING', label: 'في التجربة', kind: 'warning', hint: 'هُيّئت شركته وهو يجرّب الآن.' },
  { key: 'CONTACTED', label: 'تم التواصل', kind: 'neutral', hint: 'تحدّث معه فريق البيع.' },
  { key: 'CONVERTED', label: 'مشترك', kind: 'success', hint: 'صار عميلًا يدفع.' },
  { key: 'REJECTED', label: 'مستبعَد', kind: 'danger', hint: 'غير جاد أو لا يناسبه النظام.' },
] as const;

export const SIGNUP_STATUS_MAP: Readonly<Record<SignupStatus, SignupStatusDef>> =
  Object.fromEntries(SIGNUP_STATUS_DEFS.map((s) => [s.key, s])) as Record<
    SignupStatus,
    SignupStatusDef
  >;
